"use client";

import { useState } from "react";
import {
  Bitcoin,
  TrendingUp,
  TrendingDown,
  Calendar,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from "lucide-react";
import { ContextAnalysis, CryptoData } from "@/hooks/useAIContextAnalysis";

interface Market {
  id: string;
  question: string;
  outcomes: string[];
  outcomePrices: number[];
}

interface CryptoAISidebarProps {
  analysis: ContextAnalysis;
  eventTitle: string;
  markets?: Market[];
}

function getOutcomePrice(
  outcomeName: string,
  markets?: Market[]
): number | null {
  if (!markets) return null;
  const nameLower = outcomeName.toLowerCase();
  for (const market of markets) {
    const idx = market.outcomes.findIndex(
      (o) => o.toLowerCase() === nameLower
    );
    if (idx !== -1 && market.outcomePrices[idx] != null) {
      return market.outcomePrices[idx];
    }
  }
  return null;
}

export default function CryptoAISidebar({
  analysis,
  eventTitle,
  markets = [],
}: CryptoAISidebarProps) {
  const [showAllLevels, setShowAllLevels] = useState(false);

  const crypto = analysis.cryptoData;
  if (!crypto) return null;

  const assets = crypto.assets || [];
  const keyLevels = crypto.keyLevels || [];
  const catalysts = (crypto.catalysts || []).filter(
    (c) => !isPastDate(c.date)
  );
  const displayLevels = showAllLevels ? keyLevels : keyLevels.slice(0, 5);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-[#18181D] rounded-[5px] border border-[#F7931A]/30 overflow-hidden">
        <div
          className="px-4 py-3"
          style={{ background: "linear-gradient(to right, #F7931A, #627EEA)" }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bitcoin className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-inter-tight font-bold truncate">
                {analysis.widgetConfig.title || "Crypto Market Context"}
              </span>
            </div>
          </div>
        </div>

        {/* Assets */}
        {assets.length > 0 && (
          <div className="p-3 space-y-2">
            {assets.map((asset, idx) => {
              const price = getOutcomePrice(asset.name, markets) ||
                getOutcomePrice(asset.symbol, markets);

              return (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-2.5 rounded-[5px] bg-[#0f0f12]/50 border border-[#1A1A1A]/50 hover:border-[#F7931A]/20 transition-colors"
                >
                  <div
                    className="w-8 h-8 rounded-[5px] flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                    style={{ backgroundColor: asset.color }}
                  >
                    {asset.symbol.slice(0, 3)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-white font-satoshi font-bold text-sm">
                        {asset.name}
                      </p>
                      <span className="text-gray-500 text-[10px] font-inter-tight">
                        {asset.symbol}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {asset.currentPrice && (
                        <span className="text-gray-400 text-xs font-inter-tight">
                          {asset.currentPrice}
                        </span>
                      )}
                      {asset.marketCap && (
                        <span className="text-gray-500 text-[10px] font-inter-tight">
                          MCap: {asset.marketCap}
                        </span>
                      )}
                    </div>
                  </div>
                  {asset.priceTarget && (
                    <div className="text-right flex-shrink-0">
                      <span className="text-[#F7931A] text-[10px] font-inter-tight">
                        Target
                      </span>
                      <p className="text-white font-monument font-bold text-sm">
                        {asset.priceTarget}
                      </p>
                    </div>
                  )}
                  {price !== null && !asset.priceTarget && (
                    <span className="text-white font-monument font-bold text-sm flex-shrink-0">
                      {Math.round(price * 100)}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Key Levels */}
      {keyLevels.length > 0 && (
        <div className="bg-[#18181D] border-2 border-[#1A1A1A]/50 rounded-[5px] overflow-hidden hover:border-[#F7931A]/40 transition-colors">
          <div className="bg-[#18181D] border-b border-[#1A1A1A]/50 px-4 py-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#F7931A]" />
              <h3 className="text-white font-inter-tight font-bold text-sm uppercase tracking-wider">
                Key Levels
              </h3>
            </div>
          </div>
          <div className="p-3 space-y-2">
            {displayLevels.map((level, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between bg-[#0f0f12]/50 rounded-[5px] p-2.5"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {level.type === "resistance" ? (
                    <ArrowUpRight className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                  ) : level.type === "support" ? (
                    <ArrowDownRight className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                  ) : (
                    <Minus className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
                  )}
                  <span className="text-gray-400 text-xs font-inter-tight truncate">
                    {level.label}
                  </span>
                </div>
                <span className="text-white font-monument font-bold text-sm flex-shrink-0 ml-2">
                  {level.level}
                </span>
              </div>
            ))}

            {keyLevels.length > 5 && (
              <button
                onClick={() => setShowAllLevels(!showAllLevels)}
                className="w-full flex items-center justify-center gap-1 py-1.5 text-gray-400 hover:text-white text-xs font-inter-tight transition-colors"
              >
                {showAllLevels ? (
                  <><ChevronUp className="w-4 h-4" /> Show less</>
                ) : (
                  <><ChevronDown className="w-4 h-4" /> Show {keyLevels.length - 5} more</>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Catalysts */}
      {catalysts.length > 0 && (
        <div className="bg-[#18181D] border-2 border-[#F7931A]/20 rounded-[5px] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-[#F7931A]" />
            <h3 className="text-white font-inter-tight font-bold text-sm">
              Upcoming Catalysts
            </h3>
          </div>
          <div className="space-y-2 text-xs">
            {catalysts.slice(0, 5).map((catalyst, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-1.5 h-1.5 rounded-[5px] flex-shrink-0 ${
                      catalyst.impact === "high"
                        ? "bg-red-400"
                        : catalyst.impact === "medium"
                          ? "bg-yellow-400"
                          : "bg-gray-400"
                    }`}
                  />
                  <span className="text-gray-400 font-satoshi truncate">
                    {catalyst.event}
                  </span>
                </div>
                <span className="text-[#F7931A] font-inter-tight font-bold flex-shrink-0 ml-2">
                  {catalyst.date}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Market Context */}
      {crypto.marketContext && (
        <div className="bg-[#0f0f12]/50 border border-[#1A1A1A] rounded-[5px] p-3">
          <div className="flex items-start gap-2">
            <TrendingUp className="w-4 h-4 text-[#F7931A] mt-0.5 flex-shrink-0" />
            <p className="text-gray-400 text-[10px] font-inter-tight leading-relaxed">
              {crypto.marketContext}
            </p>
          </div>
        </div>
      )}

      {/* Historical Note */}
      {crypto.historicalNote && (
        <div className="bg-[#0f0f12]/50 border border-[#1A1A1A] rounded-[5px] p-3">
          <div className="flex items-start gap-2">
            <Bitcoin className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
            <p className="text-gray-400 text-[10px] font-inter-tight leading-relaxed">
              {crypto.historicalNote}
            </p>
          </div>
        </div>
      )}

      {/* AI Reasoning */}
      {analysis.reasoning && (
        <details className="group">
          <summary className="flex items-center gap-2 text-gray-500 text-xs font-inter-tight cursor-pointer hover:text-gray-300 transition-colors">
            <Zap className="w-3 h-3" />
            <span>Why this widget?</span>
          </summary>
          <p className="mt-2 text-gray-400 text-xs leading-relaxed pl-5">
            {analysis.reasoning}
          </p>
        </details>
      )}
    </div>
  );
}

function isPastDate(dateStr: string): boolean {
  try {
    return new Date(dateStr) < new Date();
  } catch {
    return false;
  }
}
